/**
 * Supabase Realtime subscription manager.
 * Replaces ws-client.ts WebSocket relay.
 *
 * Channels:
 * - shop-stamps:{address} (Postgres Changes) — barista receives new stamp/redeem requests
 * - customer-stamps:{address} (Postgres Changes) — customer receives approval/denial
 */
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'failed';

type StateListener = (state: ConnectionState) => void;

class RealtimeClient {
  private channels: Map<string, RealtimeChannel> = new Map();
  private _state: ConnectionState = 'disconnected';
  private stateListeners: Set<StateListener> = new Set();

  subscribeAsBarista(
    shopOwnerAddress: string,
    onStampInsert: (payload: any) => void,
    onStampUpdate: (payload: any) => void,
    onRedeemInsert: (payload: any) => void,
    onRedeemUpdate: (payload: any) => void,
  ): void {
    this.cleanup();
    this.setState('connecting');

    const stampChannel = supabase
      .channel(`shop-stamps:${shopOwnerAddress}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'stamp_requests',
          filter: `shop_owner_address=eq.${shopOwnerAddress}`,
        },
        (payload) => onStampInsert(payload.new)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stamp_requests',
          filter: `shop_owner_address=eq.${shopOwnerAddress}`,
        },
        (payload) => onStampUpdate(payload.new)
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') this.setState('connected');
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') this.setState('disconnected');
      });

    const redeemChannel = supabase
      .channel(`shop-redeems:${shopOwnerAddress}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'redeem_requests',
          filter: `shop_owner_address=eq.${shopOwnerAddress}`,
        },
        (payload) => onRedeemInsert(payload.new)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'redeem_requests',
          filter: `shop_owner_address=eq.${shopOwnerAddress}`,
        },
        (payload) => onRedeemUpdate(payload.new)
      )
      .subscribe();

    this.channels.set('shop-stamps', stampChannel);
    this.channels.set('shop-redeems', redeemChannel);
  }

  subscribeAsCustomer(
    customerAddress: string,
    onStampUpdate: (payload: any) => void,
    onRedeemUpdate: (payload: any) => void,
  ): void {
    this.cleanup();
    this.setState('connecting');

    const stampChannel = supabase
      .channel(`customer-stamps:${customerAddress}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stamp_requests',
          filter: `customer_address=eq.${customerAddress}`,
        },
        (payload) => onStampUpdate((payload as any).new || payload.old)
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') this.setState('connected');
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') this.setState('disconnected');
      });

    const redeemChannel = supabase
      .channel(`customer-redeems:${customerAddress}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'redeem_requests',
          filter: `customer_address=eq.${customerAddress}`,
        },
        (payload) => onRedeemUpdate((payload as any).new || payload.old)
      )
      .subscribe();

    this.channels.set('customer-stamps', stampChannel);
    this.channels.set('customer-redeems', redeemChannel);
  }

  cleanup(): void {
    for (const channel of this.channels.values()) {
      supabase.removeChannel(channel);
    }
    this.channels.clear();
    this.setState('disconnected');
  }

  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this._state);
    return () => { this.stateListeners.delete(listener); };
  }

  getState(): ConnectionState {
    return this._state;
  }

  isConnected(): boolean {
    return this._state === 'connected';
  }

  private setState(state: ConnectionState): void {
    if (this._state === state) return;
    this._state = state;
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }
}

export const realtimeClient = new RealtimeClient();
